import { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiCheck, FiX, FiSearch } from 'react-icons/fi';

const Dropdown = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  className = '',
  renderOption = null,
  multiple = false, // Enable multi-select mode
  searchable = null // Enable search (defaults to true for multiple, false otherwise)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Default searchable to true for multiple selects
  const isSearchable = searchable !== null ? searchable : multiple;

  // For multiple mode, value should be an array
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : value;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear search and focus input when dropdown opens/closes
  useEffect(() => {
    if (isOpen && isSearchable) {
      setSearchTerm('');
      // Focus after a small delay to ensure the input is rendered
      setTimeout(() => searchInputRef.current?.focus(), 10);
    }
  }, [isOpen, isSearchable]);

  // Filter options based on search term
  const filteredOptions = isSearchable && searchTerm
    ? options.filter(opt => {
        const label = typeof opt === 'object' ? opt.label : opt;
        return label.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : options;

  // Get display value for single select
  const getDisplayValue = () => {
    if (multiple) {
      if (selectedValues.length === 0) return placeholder;
      if (selectedValues.length === 1) {
        const opt = options.find(o => (typeof o === 'object' ? o.value : o) === selectedValues[0]);
        return typeof opt === 'object' ? opt.label : opt;
      }
      return `${selectedValues.length} selected`;
    } else {
      const selectedOption = options.find(opt => 
        typeof opt === 'object' ? opt.value === value : opt === value
      );
      return selectedOption 
        ? (typeof selectedOption === 'object' ? selectedOption.label : selectedOption)
        : placeholder;
    }
  };

  const handleSelect = (optionValue) => {
    if (multiple) {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    if (multiple) {
      onChange([]);
    }
  };

  const isSelected = (optionValue) => {
    if (multiple) {
      return selectedValues.includes(optionValue);
    }
    return optionValue === value;
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
      >
        <span className={`truncate ${(multiple ? selectedValues.length > 0 : value) ? 'text-gray-900' : 'text-gray-500'}`}>
          {getDisplayValue()}
        </span>
        <div className="flex items-center gap-1">
          {multiple && selectedValues.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="p-0.5 hover:bg-gray-200 rounded transition-colors"
            >
              <FiX className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
          <FiChevronDown 
            className={`text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {isSearchable && (
            <div className="px-2 py-2 border-b border-gray-100 sticky top-0 bg-white">
              <div className="relative">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          {multiple && (
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">{selectedValues.length} selected</span>
              {selectedValues.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
          {filteredOptions.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500 text-center">
              No results found
            </div>
          )}
          {filteredOptions.map((option, index) => {
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? option.label : option;
            const selected = isSelected(optionValue);

            return (
              <button
                key={index}
                type="button"
                onClick={() => handleSelect(optionValue)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                  selected && !multiple ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                {multiple && (
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    selected 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'border-gray-300'
                  }`}>
                    {selected && <FiCheck className="w-3 h-3 text-white" />}
                  </div>
                )}
                <span className="flex-1 flex items-center gap-2">
                  {renderOption ? renderOption(option) : optionLabel}
                </span>
                {!multiple && selected && <FiCheck className="text-blue-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
